import React, {useEffect, useState} from "react";
import {Box, Text, ChevronDownIcon} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import styled, {keyframes} from "styled-components";

import {NEXT_BEST_ACTIONS_FLAG} from "shared/constants";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {
    WIDGET_HEIGHT_COLLAPSED,
    WIDGET_HEIGHT_COLLAPSED_MOBILE,
    SMALL_SCREEN_WIDTH,
    MEDIUM_SCREEN_WIDTH,
    NEXT_BEST_ACTIONS_FLAG_DEFAULT,
    RecommendationLocation,
    useGenerateRecommendationsMutation,
} from "../NextBestActions";
import NextBestActionsCard from "../components/NextBestActionsCard";
import NextBestActionsLoading from "../components/NextBestActionsLoading";
import IconStar from "../../Icons/IconStar";
import {TickerHeader} from "../components/TickerHeader";
import useUniversityColor from "../../UniversityProvider/hooks/useUniversityColor";

const NextBestActionsContainer = styled(Box)`
    @media (max-width: ${SMALL_SCREEN_WIDTH}) {
        height: ${WIDGET_HEIGHT_COLLAPSED_MOBILE};
    }
`;

const CardContainer = styled(Box)`
    height: 229px;
    width: 247px;
    padding: 16px;
    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        width: 294px;
    }
    @media (max-width: ${SMALL_SCREEN_WIDTH}) {
        height: 211px;
        width: 100%;
    }
`;

const slideDown = keyframes`
    from {
        transform: translate3d(0, -12px, 0);
        opacity: 0;
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
`;

const CardsContainer = styled(Box)`
    & ${CardContainer}:nth-child(n+4) {
        display: none;
    }
    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        & ${CardContainer}:nth-child(3) {
            display: none;
        }
    }
    @media (max-width: ${SMALL_SCREEN_WIDTH}) {
        & ${CardContainer}:nth-child(2) {
            display: none;
        }
    }
    animation: ${slideDown} 0.2s ease;
`;

const ChevronIcon = styled(ChevronDownIcon)`
    font-size: 14px;
    transform-origin: center;
    transform: ${({isOpen}) => (isOpen ? "rotate(-180deg)" : "rotate(0)")};
    transition: 0.2s ease transform;
`;

const ToggleSectionContainer = styled(Box)<{isOpen: boolean}>`
    border-radius: 4px;
    border-bottom-left-radius: ${(props) => (props.isOpen ? "0px" : "4px")};
    border-bottom-right-radius: ${(props) => (props.isOpen ? "0px" : "4px")};

    &:hover:not([disabled]) {
        cursor: pointer;
        opacity: ${(props) => (props.isOpen ? "1" : "0.85")};
    }
`;

const ToggleSectionButtonContainer = styled(Box)<{isOpen: boolean}>`
    border-radius: 0px;
    border-top-left-radius: ${(props) => (props.isOpen ? "4px" : "0px")};
    border-top-right-radius: ${(props) => (props.isOpen ? "4px" : "0px")};
`;

const ToggleSectionContents = styled(Box)<{isOpen: boolean}>`
    border-radius: 4px;
    border-top-left-radius: ${(props) => (props.isOpen ? "0px" : "4px")};
    border-top-right-radius: ${(props) => (props.isOpen ? "0px" : "4px")};
`;

const StyledHeaderText = styled(Text)`
    height: 18px;
    &:before {
        content: "Recommended for you";
    }
    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        &:before {
            content: "For you";
        }
    }
`;

export default function NextBestActions() {
    const SHOW_NEXT_BEST_ACTIONS = useBooleanFeatureFlag(
        NEXT_BEST_ACTIONS_FLAG,
        NEXT_BEST_ACTIONS_FLAG_DEFAULT,
    );

    const {reportError} = useErrorReporting();

    const {trackEvent} = useAnalytics();

    const [isOpen, setIsOpen] = useState(false);

    const {id: universityId, name: universityName, slug: universitySlug} = useUniversity();
    const color = useUniversityColor();

    const [getGeneratedRecommendations, {data, loading, error}] =
        useGenerateRecommendationsMutation({
            variables: {
                universityId,
                batchSize: 3,
            },
        });

    const getRecommendations = React.useCallback(async () => {
        try {
            await getGeneratedRecommendations();
        } catch (e) {
            console.error("Error generating NBA recommendations", e);
        }
    }, [getGeneratedRecommendations]);

    useEffect(() => {
        if (!SHOW_NEXT_BEST_ACTIONS) {
            trackEvent(sharedEvents.nextBestActionsNotShown.name, {
                universityId,
                universityName,
                where: RecommendationLocation.WIDGET_CHAT,
            });
        } else {
            trackEvent(sharedEvents.nextBestActionsShown.name, {
                universityId,
                universityName,
                where: RecommendationLocation.WIDGET_CHAT,
            });
            getRecommendations();
        }
    }, [getRecommendations, SHOW_NEXT_BEST_ACTIONS, trackEvent, universityId, universityName]);

    if (!SHOW_NEXT_BEST_ACTIONS) return null;

    if (loading) return <NextBestActionsLoading />;

    if (error) {
        reportError(error, {
            tags: {universitySlug},
        });
        return null;
    }

    if (!data || data.generateRecommendations.length === 0) {
        return null;
    }

    return (
        <NextBestActionsContainer
            position="relative"
            h={WIDGET_HEIGHT_COLLAPSED}
            w="100%"
            marginBottom="small"
            data-test-id="nba-container"
        >
            <ToggleSectionContainer
                borderWidth={1}
                borderRadius="xxsmall"
                borderColor={color}
                w="100%"
                borderBottomWidth={isOpen ? 0 : 1}
                isOpen={isOpen}
            >
                <ToggleSectionButtonContainer isOpen={isOpen} bgColor={isOpen ? "white" : color}>
                    <Box
                        w="100%"
                        height={WIDGET_HEIGHT_COLLAPSED}
                        as="button"
                        onClick={() => {
                            trackEvent(sharedEvents.nextBestActionsToggleButtonClicked.name, {
                                universityId,
                                universityName,
                                where: RecommendationLocation.WIDGET_CHAT,
                            });
                            setIsOpen(!isOpen);
                        }}
                        borderRadius="xxsmall"
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box
                            display="flex"
                            w="80%"
                            flexDirection="row"
                            justifyContent="flex-start"
                            gap="medium"
                        >
                            <Box
                                display="inline-flex"
                                flexDirection="row"
                                alignItems="center"
                                marginLeft="small"
                            >
                                <Box w="19px" h="18px" paddingRight="xxsmall" marginRight="xxsmall">
                                    <IconStar color={isOpen ? color : "white"} />
                                </Box>

                                <StyledHeaderText
                                    transform="uppercase"
                                    size="small"
                                    color={isOpen ? color : "white"}
                                    weight="700"
                                    nowrap
                                />
                            </Box>
                            {isOpen ? null : (
                                <Box w="60%">
                                    <TickerHeader data={data} />
                                </Box>
                            )}
                        </Box>
                        <Box
                            w="20%"
                            marginRight="xsmall"
                            display="flex"
                            flexDirection="row-reverse"
                            alignItems="flex-end"
                            paddingRight="small"
                        >
                            <ChevronIcon isOpen={isOpen} color={isOpen ? color : "white"} />
                        </Box>
                    </Box>
                </ToggleSectionButtonContainer>
                <ToggleSectionContents
                    display={isOpen ? "block" : "none"}
                    isOpen={isOpen}
                    w="100%"
                    bgColor="white"
                    zIndex={99}
                    borderWidth={1}
                    borderTopWidth={0}
                    borderColor={color}
                    position="absolute"
                    top={WIDGET_HEIGHT_COLLAPSED}
                    left={0}
                >
                    <CardsContainer
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        gap="small"
                        padding="small"
                        bgColor="white"
                        borderRadius="xxsmall"
                    >
                        {data.generateRecommendations.map((content) => {
                            return (
                                <CardContainer
                                    borderWidth={1}
                                    borderColor="#D0D9D9"
                                    borderRadius="xxsmall"
                                    key={content.id}
                                >
                                    <NextBestActionsCard
                                        contentId={content.contentId}
                                        title={content.title}
                                        description={content.description}
                                        contentType={content.type}
                                        url={content.url}
                                        batchId={content.batchId}
                                    />
                                </CardContainer>
                            );
                        })}
                    </CardsContainer>
                </ToggleSectionContents>
            </ToggleSectionContainer>
        </NextBestActionsContainer>
    );
}
